import { useEffect, useState } from "react";
import client from "../lib/api";
import { money, fmtDate, onboardingLink, ONBOARDING_BASE } from "../lib/helpers";
import { Gift, Copy, CheckCircle, LinkSimple, PencilSimple, Wallet, ChatText, EnvelopeSimple, ShareNetwork, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

export default function ReferralPanel() {
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => client.get("/referral/me").then((r) => { setD(r.data); setSlug(r.data.slug); });
  useEffect(() => { load(); }, []);

  if (!d) return <p className="text-[#5C5A58]">Loading…</p>;

  const fullLink = onboardingLink("agent", d.slug);
  const safeCopy = async (text, label) => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement("textarea"); ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success(`${label} copied`);
    } catch { toast.message(`${label} ready`, { description: "Press ⌘/Ctrl+C to copy." }); }
  };
  const copy = () => safeCopy(fullLink, "Referral link");

  const bookLink = `${ONBOARDING_BASE}/book?ref=${d.slug}`;
  const shareMsg = `Hi! I shoot all my listings with Reelty — fast turnaround and stunning photo/video. Book your shoot here and we both get a perk: ${bookLink}`;
  const copyText = (text, label) => safeCopy(text, label);
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Book with Reelty", text: shareMsg, url: bookLink }); } catch {} }
    else safeCopy(shareMsg, "Message");
  };

  const saveSlug = async () => {
    setSaving(true);
    try {
      await client.post("/referral/slug", { slug: slug.trim().toLowerCase() });
      toast.success("Referral link updated");
      setEditing(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update link"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="referral-panel">
      {/* Credit balance hero */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-[#B08D57]/40 rounded-md p-4 bg-[#B08D57]/[0.08] shadow-premium" data-testid="referral-balance-card">
          <div className="flex items-center gap-2 text-[#8a6d0a] eyebrow mb-2"><Wallet size={16} weight="duotone" />Account credit</div>
          <p className="font-display text-3xl tabular-nums text-[#8a6d0a]" data-testid="referral-balance">{money(d.credit_balance)}</p>
          <p className="text-xs text-[#5C5A58] mt-0.5">Applied automatically to your next invoice</p>
        </div>
        <div className="border border-[#E9DDC6] rounded-md p-4 bg-white shadow-premium">
          <div className="flex items-center gap-2 text-[#5C5A58] eyebrow mb-2"><Gift size={16} weight="duotone" color="#4A0000" />Total earned</div>
          <p className="font-display text-3xl tabular-nums text-[#4A0000]" data-testid="referral-earned">{money(d.earned_total)}</p>
          <p className="text-xs text-[#5C5A58] mt-0.5">{d.referral_count} referral{d.referral_count === 1 ? "" : "s"} booked</p>
        </div>
        <div className="border border-[#E9DDC6] rounded-md p-4 bg-white shadow-premium">
          <div className="flex items-center gap-2 text-[#5C5A58] eyebrow mb-2"><CheckCircle size={16} weight="duotone" color="#2e7d32" />Credit used</div>
          <p className="font-display text-3xl tabular-nums text-[#2e7d32]" data-testid="referral-applied">{money(d.applied_total)}</p>
          <p className="text-xs text-[#5C5A58] mt-0.5">Across your paid invoices</p>
        </div>
      </div>

      {/* One-tap share card */}
      <div className="rounded-md p-6 shadow-premium border border-[#B08D57]/45 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #4A0000 0%, #5C1A1A 60%, #2A0A0A 100%)" }} data-testid="referral-share-card">
        <div className="pointer-events-none absolute -top-16 -right-14 w-52 h-52 rounded-full bg-[#B08D57]/25 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 text-[#E4C97E] eyebrow mb-1"><PaperPlaneTilt size={17} weight="fill" />Share &amp; earn</div>
          <h3 className="font-display text-2xl text-white whitespace-nowrap">Send this to another agent</h3>
          <p className="text-sm text-white/65 mt-1">Share your link — you earn <span className="text-[#E4C97E] font-medium">10% credit</span> on every shoot booked through it, applied straight to your next invoice.</p>

          {/* Priority action: copy the referral link */}
          <div className="mt-4" data-testid="referral-link-card">
            <label className="text-[11px] uppercase tracking-[0.14em] text-white/50">Your referral link</label>
            <div className="mt-1.5 flex flex-col sm:flex-row gap-2">
              <code data-testid="referral-link" className="flex-1 min-w-0 flex items-center bg-white/[0.08] border border-white/20 rounded-md px-3 py-2.5 text-sm text-white font-mono-num truncate">{fullLink}</code>
              <div className="flex gap-2">
                <Button data-testid="referral-copy" onClick={copy}
                  className="flex-1 sm:flex-none justify-center bg-gradient-to-r from-[#C6A253] to-[#B08D57] text-[#1A1715] hover:from-[#D0AC5C] hover:to-[#B99A5B] font-semibold">
                  <Copy size={16} className="mr-1.5" weight="bold" />Copy link
                </Button>
                <Button data-testid="referral-edit" variant="outline" onClick={() => setEditing(true)}
                  className="justify-center bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
                  <PencilSimple size={15} className="mr-1" />Customize
                </Button>
              </div>
            </div>
          </div>

          {/* Secondary: send a ready-made message */}
          <div className="mt-5 pt-5 border-t border-white/10">
            <p className="text-xs text-white/55 mb-2">Prefer to send a ready-made message?</p>
            <div className="bg-white/[0.06] border border-white/15 rounded-lg p-3.5">
              <p className="text-sm text-white/90 leading-relaxed" data-testid="referral-share-message">{shareMsg}</p>
            </div>
            <div className="mt-3 flex flex-col sm:flex-row sm:flex-wrap gap-2">
              <Button data-testid="share-copy-message" variant="outline" onClick={() => copyText(shareMsg, "Message")}
                className="w-full sm:w-auto justify-center bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
                <Copy size={16} className="mr-1.5" weight="bold" />Copy message
              </Button>
              <a data-testid="share-text" href={`sms:?&body=${encodeURIComponent(shareMsg)}`}
                className="w-full sm:w-auto justify-center inline-flex items-center rounded-md border border-white/30 text-white hover:bg-white/10 transition-colors px-3 py-2 text-sm"><ChatText size={16} className="mr-1.5" weight="fill" />Text</a>
              <a data-testid="share-email" href={`mailto:?subject=${encodeURIComponent("Book your listing shoot with Reelty")}&body=${encodeURIComponent(shareMsg)}`}
                className="w-full sm:w-auto justify-center inline-flex items-center rounded-md border border-white/30 text-white hover:bg-white/10 transition-colors px-3 py-2 text-sm"><EnvelopeSimple size={16} className="mr-1.5" weight="fill" />Email</a>
              <Button data-testid="share-native" variant="outline" onClick={nativeShare}
                className="w-full sm:w-auto justify-center bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white sm:hidden">
                <ShareNetwork size={16} className="mr-1.5" />Share
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Referrals list */}
      <div className="lux-panel overflow-hidden">
        <div className="bg-[#FAF9F6] px-4 py-3 eyebrow text-[#5C5A58]">Your referrals</div>
        <table className="w-full text-sm" data-testid="referral-rows">
          <thead>
            <tr className="text-left text-[#5C5A58] border-b border-[#E9DDC6]">
              <th className="p-4 font-medium">Property</th>
              <th className="p-4 font-medium">Booked</th>
              <th className="p-4 font-medium">Your credit</th>
              <th className="p-4 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {d.referrals.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#5C5A58]">No referrals yet. Share your link to start earning credit.</td></tr>}
            {d.referrals.map((r, i) => (
              <tr key={r.property_id || `book-${i}`} className={i % 2 ? "bg-[#FBFAF7]" : "bg-white"} data-testid={`referral-row-${r.property_id || `book-${i}`}`}>
                <td className="p-4 font-medium">{r.title}{r.via === "booking" && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#8A6D1B] bg-[#B08D57]/12 border border-[#B08D57]/30 rounded px-1.5 py-0.5">via /book</span>}</td>
                <td className="p-4">{fmtDate(r.created_at ? r.created_at.slice(0, 10) : r.shoot_date)}</td>
                <td className="p-4 font-mono-num font-semibold text-[#8a6d0a]">{money(r.credit)}</td>
                <td className="p-4">{r.paid
                  ? <span className="pill pill--paid">Shoot paid</span>
                  : <span className="pill pill--gold">Credit earned</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent data-testid="referral-slug-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Customize your link</DialogTitle>
            <DialogDescription>Choose a short, memorable link (3–15 lowercase letters, numbers or hyphens).</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-[#9A968E]">{ONBOARDING_BASE}/book?ref=</span>
            <Input data-testid="referral-slug-input" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} className="flex-1" />
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="referral-slug-cancel" onClick={() => setEditing(false)}>Cancel</Button>
            <Button data-testid="referral-slug-save" disabled={saving} onClick={saveSlug} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{saving ? "Saving…" : "Save link"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
